import { useState } from 'react';
import { usePageTab } from '../../shell/Shell';
import { A, st, useContentHeight, useFlushContent } from './ui';
import { RichChat, type Lens } from './RichChat';
import { MemorySection, QuietSection, RulesSection, WhySection } from './RichSections';

// Atlas > Conversations for Operator (and Partner, read only), Facility Admin and Care Staff
// (Console V6 "Atlas · Conversations", isStaffRole): staff read residents' conversations
// here. The same surface, with the sections each lens holds. V6 moved Care Staff's "Before
// your round" to the Atlas panel's Today tab (tdRoundShow).

type Sec = 'chat' | 'memory' | 'why' | 'rules' | 'quiet';

export const SECTIONS: Record<Lens, [Sec, string][]> = {
  care: [
    ['chat', 'Conversations'],
    ['memory', 'What it knows'],
  ],
  admin: [
    ['chat', 'Conversations'],
    ['memory', 'What it knows'],
    ['why', 'Why it did that'],
    ['rules', 'Standing instructions'],
    ['quiet', 'Who has gone quiet'],
  ],
  op: [
    ['chat', 'Conversations'],
    ['memory', 'What it knows'],
    ['why', 'Why it did that'],
    ['quiet', 'Who has gone quiet'],
  ],
};

const tabStyle = (on: boolean) =>
  `display:inline-flex;align-items:center;height:34px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;${on ? 'color:#FCFAF4;background:#0F6B63;' : 'color:#3C454F;background:transparent;'}`;

/**
 * `embedded`: inside the Atlas page, under its own header and tabs (V6), so it keeps the
 * console's padding and takes the room left under the tabs instead of the whole pane.
 */
export function RichCompanion({ lens, embedded = false }: { lens: Lens; embedded?: boolean }) {
  const [sec, setSec] = useState<Sec>('chat');
  const [selected, setSelected] = useState<string | null>(null);
  const [who, setWho] = useState<string | null>(null);
  useFlushContent(!embedded);
  const paneH = useContentHeight();
  const tabs = SECTIONS[lens];
  usePageTab(embedded ? null : (tabs.find((t) => t[0] === sec)?.[1] ?? null));
  const height = embedded ? '100%' : paneH !== null ? paneH + 'px' : '100%';
  return (
    <div data-testid={`companion-${lens}`} style={st(`display:flex;flex-direction:column;box-sizing:border-box;height:${height};min-height:0;min-width:0;padding:12px 24px 0;`)}>
      <div role="tablist" aria-label="Conversations sections" style={st('flex:0 0 auto;display:flex;align-items:center;gap:4px;flex-wrap:wrap;row-gap:4px;min-width:0;padding-bottom:10px;')}>
        {tabs.map(([id, label]) => (
          <A key={id} role="tab" selected={sec === id} onClick={() => setSec(id)} s={tabStyle(sec === id)}>
            {label}
          </A>
        ))}
      </div>
      {sec === 'chat' && (
        <RichChat
          lens={lens}
          selected={selected}
          onSelect={setSelected}
          onGoMemory={() => {
            if (selected) setWho(selected);
            setSec('memory');
          }}
        />
      )}
      {sec === 'memory' && <MemorySection who={who} setWho={setWho} />}
      {sec === 'why' && <WhySection lens={lens} who={who} setWho={setWho} />}
      {sec === 'rules' && <RulesSection />}
      {sec === 'quiet' && <QuietSection />}
    </div>
  );
}
